import { useCallback } from 'react';
import type Canvas from 'diagram-js/lib/core/Canvas';
import { useDiagramContext } from '../Context/DiagramContext';
import ElementRegistry from 'diagram-js/lib/core/ElementRegistry';

export const useDiagramZoom = () => {
  const {
    bpmnModelerRef,
    containerRef,
    currentScale,
    setCurrentScale,
    isFitViewToggled,
    setIsFitViewToggled,
  } = useDiagramContext();

  const handleZoomIn = useCallback(() => {
    if (bpmnModelerRef.current && containerRef.current) {
      const canvas = bpmnModelerRef.current.get('canvas') as Canvas;
      const rect = containerRef.current.getBoundingClientRect();
      const center = {
        x: rect.width / 2,
        y: rect.height / 2,
      };
      const newScale = Math.min(currentScale + 0.1, 2);
      canvas.zoom(newScale, center);
      setCurrentScale(newScale);
    }
  }, [bpmnModelerRef, containerRef, currentScale, setCurrentScale]);

   const handleZoomOut = useCallback(() => {
  if (bpmnModelerRef.current && containerRef.current) {
    const canvas = bpmnModelerRef.current.get('canvas') as Canvas;

    const rect = containerRef.current.getBoundingClientRect();
    const center = {
      x: rect.width / 2,
      y: rect.height / 2,
    };

    const currentZoom = canvas.zoom();
    const newScale = Math.max(currentZoom - 0.1, 0.2);

    canvas.zoom(newScale, center);
    setCurrentScale(newScale);
  }
}, [bpmnModelerRef, containerRef, setCurrentScale]);
  const handleFitViewToggle = useCallback(() => {
    if (bpmnModelerRef.current) {
      const canvas = bpmnModelerRef.current.get('canvas') as Canvas;
      canvas.zoom('fit-viewport' as any, 'auto' as any);
      setCurrentScale(1);
      setIsFitViewToggled(!isFitViewToggled);
    }
  }, [bpmnModelerRef, isFitViewToggled, setCurrentScale, setIsFitViewToggled]);

  const handleInitialFitView = useCallback(() => {
    if (bpmnModelerRef.current) {
      const canvas = bpmnModelerRef.current.get('canvas') as Canvas;
      canvas.resized();
      canvas.zoom('fit-viewport', 'auto' as any);
      setCurrentScale(1);
    }
  }, [bpmnModelerRef, setCurrentScale]);

  const centerDiagram = useCallback(() => {
    if (!bpmnModelerRef.current || !containerRef.current) return;

    const canvas = bpmnModelerRef.current.get('canvas') as Canvas;
    const elementRegistry = bpmnModelerRef.current.get('elementRegistry') as ElementRegistry;
    const definitions = elementRegistry.getAll();

    if (definitions.length === 0) return;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    definitions.forEach((element: any) => {
      if (element.x !== undefined && element.y !== undefined) {
        minX = Math.min(minX, element.x);
        minY = Math.min(minY, element.y);
        maxX = Math.max(maxX, element.x + (element.width || 0));
        maxY = Math.max(maxY, element.y + (element.height || 0));
      }
    });

    const padding = 50;
    const diagramWidth = maxX - minX + padding * 2;
    const diagramHeight = maxY - minY + padding * 2;
    const diagramCenterX = minX + (maxX - minX) / 2;
    const diagramCenterY = minY + (maxY - minY) / 2;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    const scaleX = containerWidth / diagramWidth;
    const scaleY = containerHeight / diagramHeight;
    const scale = Math.min(scaleX, scaleY) * 0.55;

    canvas.zoom(scale);

    const viewboxWidth = containerWidth / scale;
    const viewboxHeight = containerHeight / scale;

    canvas.viewbox({
      x: diagramCenterX - viewboxWidth / 2,
      y: diagramCenterY - viewboxHeight / 2,
      width: viewboxWidth,
      height: viewboxHeight,
    });

    setCurrentScale(scale);
  }, [bpmnModelerRef, containerRef, setCurrentScale]);

  return {
    handleZoomIn,
    handleZoomOut,
    handleFitViewToggle,
    handleInitialFitView,
    centerDiagram,
  };
};
